import { useEffect, useRef } from 'react'

import type { OlarkOperatorCommandEvent } from './useOlarkCommands.js'

export type OlarkEventMap = {
  beginConversation: void
  boxExpand: void
  boxShrink: void
  messageToOperator: void
  messageToVisitor: void
  operatorCommand: OlarkOperatorCommandEvent
}

type OlarkEventHandler<K extends keyof OlarkEventMap> =
  OlarkEventMap[K] extends void
    ? () => void
    : (payload: OlarkEventMap[K]) => void

const listeners: {
  [K in keyof OlarkEventMap]: Set<OlarkEventHandler<K>>
} = {
  beginConversation: new Set(),
  boxExpand: new Set(),
  boxShrink: new Set(),
  messageToOperator: new Set(),
  messageToVisitor: new Set(),
  operatorCommand: new Set(),
}

const subscribeOlarkEvent = <K extends keyof OlarkEventMap>(
  event: K,
  handler: OlarkEventHandler<K>,
): (() => void) => {
  listeners[event].add(handler)

  return () => {
    listeners[event].delete(handler)
  }
}

export const emitOlarkEvent = <K extends keyof OlarkEventMap>(
  event: K,
  ...args: OlarkEventMap[K] extends void ? [] : [OlarkEventMap[K]]
): void => {
  for (const handler of listeners[event]) {
    if (args.length === 0) {
      ;(handler as () => void)()
    } else {
      ;(handler as (payload: OlarkEventMap[K]) => void)(args[0])
    }
  }
}

export const useOlarkEvent = <K extends keyof OlarkEventMap>(
  event: K,
  handler: OlarkEventHandler<K>,
): void => {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    const listener = ((...args: unknown[]) => {
      ;(handlerRef.current as (...listenerArgs: unknown[]) => void)(...args)
    }) as OlarkEventHandler<K>

    return subscribeOlarkEvent(event, listener)
  }, [event])
}
